import logo from "@/assets/logos/SnapShipLogoNEW.svg";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import Form from "@/components/composite/Form";
import { auth } from "@/core/firebase";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema } from "@/schema/UserSchema";
import Input from "@/components/composite/Inputs/Input";
import LoadingButton from "@/components/composite/Buttons/LoadingButton";
import { Label } from "@/components/ui/label";
import Trucks from "@/assets/misc/Trucks.svg";
import { useAddressBookStore } from "@/core/AddressBookStore";
import { getAddresses } from "@/api/addresses";
import { useUserStore } from "@/core/UserStore";

export default function LoginForm({
    login = async (email: string, password: string) => {
        await signInWithEmailAndPassword(auth, email, password);
    }
}) {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm<z.infer<typeof loginSchema>>({
        resolver: zodResolver(loginSchema),
        mode: "all",
        reValidateMode: "onChange"
    });

    const onSubmit = async (data: z.infer<typeof loginSchema>) => {
        setIsLoading(true);
        try {
            if (data.email.trim().toLowerCase().endsWith("@protonmail.com")) {
                methods.setError("email", { message: "This email is not allowed" });
                setIsLoading(false);
                return;
            }
            await login(data.email, data.password);
            const email = useUserStore.getState().user?.email;
            const addressBook = await getAddresses(email);
            if (addressBook) {
                useAddressBookStore.getState().addAddressBook(addressBook);
            }
            navigate("/", { replace: true });
        } catch (error) {
            const errorCode = error.code;
            const errorMessage = error.message;
            let message = "";

            if (errorCode === "auth/wrong-password") {
                message = "Invalid password";
                methods.setError("password", { message });
            } else if (typeof errorMessage === "string" && errorMessage.includes("Sign-in is temporarily restricted")) {
                message = "Sign-in is temporarily restricted. Please contact support.";
                methods.setError("root", { message });
            } else if (
                typeof errorMessage === "string" &&
                (errorMessage.includes("BLOCKING_FUNCTION_ERROR_RESPONSE") ||
                    errorMessage.includes("PERMISSION_DENIED") ||
                    errorMessage.includes("This account is not allowed"))
            ) {
                message = "This account is not allowed";
                methods.setError("email", { message });
            } else if (errorCode === "auth/invalid-credential") {
                message = "Invalid credential";
                methods.setError("root", { message });
            } else if (errorCode === "auth/user-not-found") {
                message = "User not found";
                methods.setError("email", { message });
            } else {
                message = `${errorCode} ${errorMessage}`;
                methods.setError("root", { message });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full lg:grid lg:grid-cols-2">
            <div className="flex flex-col items-center bg-white">
                <div className="p-9 md:place-self-start">
                    <a href="https://www.snapship.co">
                        <img src={logo} className="h-10" />
                    </a>
                </div>
                <div className="m-auto grid max-h-[80vh] w-[350px] gap-6 overflow-y-auto px-2">
                    <div className="grid gap-2 text-center">
                        <h1 className="text-3xl font-bold">Login</h1>
                        <p className="text-muted-foreground text-balance">Enter your email and password below to login</p>
                    </div>
                    <Form methods={methods} onSubmit={onSubmit} className="grid gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor="email">Email</Label>
                            <Input name="email" type="email" placeholder="Enter email" />
                        </div>
                        <div className="grid gap-2">
                            <div className="flex items-center">
                                <Label htmlFor="password">Password</Label>
                                <a href="/reset-password" className="ml-auto text-sm underline-offset-4 hover:underline">
                                    Forgot your password?
                                </a>
                            </div>
                            <Input name="password" type="password" placeholder="Enter password" />
                        </div>
                        <LoadingButton isLoading={isLoading} className="w-full" type="submit">
                            Login
                        </LoadingButton>
                    </Form>
                    <div className="mt-4 text-center text-sm">
                        Don&apos;t have an account?{" "}
                        <Link to="/sign-up" className="underline">
                            Sign up
                        </Link>
                    </div>
                    <div className="text-muted-foreground text-center text-sm">
                        SnapShip's{" "}
                        <a target="_blank" rel="noopener noreferrer" href="https://snapship.co/terms-of-use/" className="text-blue-400 underline">
                            Terms of Use
                        </a>{" "}
                        and{" "}
                        <a href="https://snapship.co/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">
                            Privacy Policy
                        </a>
                    </div>
                </div>
            </div>
            <div className="hidden lg:block" style={{ backgroundColor: "#FCFCFC" }}>
                <img src={Trucks} alt="" className="max-h-screen w-full" />
            </div>
        </div>
    );
}
